/**
 * Pure query-shape helpers for the Algolia-backed reference listing.
 *
 * Kept out of `algolia.ts` so the parameter building and facet folding can be
 * unit-tested without pulling in the Algolia client (and the `import.meta.env`
 * credential read that happens at its module scope). Mirrors the existing
 * `siblingReferences.ts` split.
 */

/**
 * Attributes the reference listing renders per row. Requested explicitly so the
 * response carries roughly a tenth of a full indexed record — the index holds
 * ~70 attributes per ref, of which the grid and list views read these.
 *
 * `movementType` and `caseSizeMm` are what let the LIST view render from hits
 * rather than from full Firestore documents; `moduleId` / `moduleNumber`
 * (watchlock #860) are what let it render the Module column, which was the last
 * thing the Firestore listing could show and this one could not.
 */
export const REFERENCE_LIST_ATTRIBUTES = [
  'objectID',
  'brandId',
  'referenceId',
  'reference',
  'model',
  'collection',
  'productName',
  'calibre',
  'calibreTierId',
  'caseSizeMm',
  'movementType',
  'moduleId',
  'moduleNumber',
  'imageUrl',
  // Retrieval only — NOT a facet, so it is requested here and must never be
  // added to BRAND_LISTING_FACETS until the index declares it. The indexer
  // (watchlock `syncReferenceToAlgolia`) does not project it yet, so today no
  // hit carries it and the row badge reads "unknown"; asking for an attribute
  // the index lacks is harmless. Once the indexer writes it, the badge lights
  // up with no change here.
  'descriptionSource',
] as const;

/**
 * Server-side sorts the listing can ask for, and the virtual-replica suffix each
 * maps to. `null` is the master index — relevance order, which is the only
 * sensible order for a text search.
 *
 * Only `createdAt` has replicas on this index (watchlock #860, declared VIRTUAL
 * so the ~107K records aren't duplicated against the record quota). Reference /
 * model / collection have none, so those sorts still reorder the loaded rows
 * client-side, exactly as they always have.
 */
export const REFERENCE_SORT_REPLICAS = {
  createdAt_desc: '_createdAt_desc',
  createdAt_asc: '_createdAt_asc',
} as const;

export type ReferenceSortKey = keyof typeof REFERENCE_SORT_REPLICAS;

/** Is this field one the index can sort server-side? */
export function isServerSortable(field: string): boolean {
  return field === 'createdAt';
}

/**
 * Facets the listing requests for its filters and counts. All four are declared
 * `attributesForFaceting` on the live index (verified against it); requesting a
 * non-faceted attribute would 400 the whole search, so this list must only ever
 * grow alongside the index config, which lives server-side in watchlock.
 *
 * `hasImage` joined once watchlock #860 made it a facet. It is a boolean written
 * unconditionally by `syncReferenceToAlgolia` — both values always present — so
 * `hasImage:false` is an exact filter and its facet count an exact tally.
 * `imageUrl` itself is still NOT faceted; filter on the boolean, never the URL.
 */
export const BRAND_LISTING_FACETS = [
  'collection',
  'calibre',
  'movementType',
  'hasImage',
] as const;

export type BrandListingFacetName = (typeof BRAND_LISTING_FACETS)[number];

/** Filters the reference listing can apply. All are plain facet filters. */
export interface ReferenceListFilters {
  brandId?: string;
  collection?: string;
  /**
   * Raw stored `calibre` strings to OR together. The dropdown folds case and
   * diacritic variants into one option, so the selected option's every raw
   * spelling has to be sent — matching on the folded display string alone would
   * depend on Algolia's facet-matching case rules.
   */
  calibreValues?: string[];
  movementType?: string;
  /** Only references the indexer recorded as having no picture. */
  onlyWithoutImage?: boolean;
}

/**
 * One distinct value of a brand-scoped facet, ready to drive a filter dropdown.
 */
export interface BrandFacetValue {
  /** Display form — the first raw value seen for this entry. */
  value: string;
  /** References carrying this value, summed across every folded raw variant. */
  count: number;
  /**
   * Every raw stored facet value folded into this entry. Pass the whole array as
   * one Algolia OR group (`[['calibre:A', 'calibre:a']]`) so the filter matches
   * all variants without relying on Algolia's facet-matching case rules.
   */
  rawValues: string[];
}

/**
 * Fold case + diacritics so "Elaboré", "elabore" and "ELABORE" all compare
 * equal.
 */
export function foldForMatch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritical marks
    .toLowerCase();
}

/**
 * Turn one Algolia facet-count map into sorted dropdown options.
 *
 * `foldCase` merges values differing only by case/diacritics into a single entry
 * (first spelling wins for display, counts summed), which is what the calibre
 * dropdown has always done. Collections are NOT folded — they were exact-matched
 * before and stay that way, since two collections differing only in case are
 * plausibly two real collections.
 *
 * Empty/whitespace values are dropped: Algolia omits records with no value for a
 * facet, but the indexer writes `''` for absent fields, which faceting then
 * reports as a legitimate value.
 */
export function foldFacetValues(
  facet: Record<string, number> | undefined,
  opts?: { foldCase?: boolean },
): BrandFacetValue[] {
  const merged = new Map<string, BrandFacetValue>();
  for (const [raw, count] of Object.entries(facet ?? {})) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = opts?.foldCase ? foldForMatch(trimmed) : trimmed;
    const existing = merged.get(key);
    if (existing) {
      existing.count += count;
      if (!existing.rawValues.includes(raw)) existing.rawValues.push(raw);
    } else {
      merged.set(key, { value: trimmed, count, rawValues: [raw] });
    }
  }
  return Array.from(merged.values()).sort((a, b) =>
    a.value.localeCompare(b.value),
  );
}

/**
 * Algolia search parameters for one page of the reference listing.
 *
 * Every filter is a separate `facetFilters` group, so groups AND together while
 * the calibre variants inside their own group OR — picking a collection AND a
 * calibre narrows to the intersection.
 *
 * `attributesToHighlight: []` because no listing surface renders highlight
 * markup; suppressing it keeps the response small. `analytics: false` keeps
 * admin browsing out of the app's search analytics. `cacheable: false` bypasses
 * the SDK's in-memory response cache so returning to the listing after editing a
 * ref shows the edit rather than replaying the pre-edit response.
 */
export function buildReferenceListParams(
  filters: ReferenceListFilters,
  page: number,
  hitsPerPage: number,
): Record<string, unknown> {
  const facetFilters: string[][] = [];
  if (filters.brandId) facetFilters.push([`brandId:${filters.brandId}`]);
  if (filters.collection) {
    facetFilters.push([`collection:${filters.collection}`]);
  }
  const calibreValues = (filters.calibreValues ?? []).filter((v) => !!v);
  if (calibreValues.length > 0) {
    facetFilters.push(calibreValues.map((v) => `calibre:${v}`));
  }
  if (filters.movementType) {
    facetFilters.push([`movementType:${filters.movementType}`]);
  }
  if (filters.onlyWithoutImage) {
    facetFilters.push(['hasImage:false']);
  }

  const params: Record<string, unknown> = {
    hitsPerPage,
    page,
    analytics: false,
    cacheable: false,
    attributesToRetrieve: [...REFERENCE_LIST_ATTRIBUTES],
    attributesToHighlight: [],
  };
  if (facetFilters.length > 0) params.facetFilters = facetFilters;
  return params;
}

/**
 * Does this reference name no calibre at all?
 *
 * Both halves of the link have to be empty: the `calibreId` FK (the modern
 * link, written by every calibre write path since Phase 5a) and the legacy
 * free-text `calibre` string. Either one being set counts as HAVING a calibre —
 * a ref linked only by FK is fully enriched, and one carrying only a string is
 * a link waiting to be made rather than a gap in the data.
 *
 * `calibreCache` is deliberately NOT consulted. It is a denormalised snapshot of
 * whatever `calibreId` points at and is undefined when there is no id, so it can
 * only ever agree with the FK — except on a ref whose id was cleared and whose
 * cache was left behind, which genuinely does need a calibre again.
 *
 * Both fields are optional on `WatchReference`, so both are guarded before the
 * trim rather than after.
 */
export function referenceHasNoCalibre(ref: {
  calibreId?: string;
  calibre?: string;
}): boolean {
  return !ref.calibreId?.trim() && !ref.calibre?.trim();
}

/**
 * Does this reference's description still need an AI pass?
 *
 * True for everything except a description that has already been AI-rewritten
 * (`'ai'`) or was written by hand (`'manual'`, which AI passes must leave
 * alone). So raw imported text counts, and so does a ref with no provenance at
 * all — a legacy ref that predates the field, treated as machine-made.
 *
 * This is the one definition of "needs AI": the Description Rewrite tool's
 * candidate test (`isDescriptionRewriteCandidate`) delegates here, so the
 * listing's filter and the tool that works the list can never disagree.
 */
export function referenceNeedsAiEnrichment(ref: {
  descriptionSource?: string | null;
}): boolean {
  return ref.descriptionSource !== 'ai' && ref.descriptionSource !== 'manual';
}

/**
 * Does this reference have no picture — by the INDEXER's rule, not by a
 * reasonable-sounding one?
 *
 * The Firestore fallback path has to agree with `hasImage:false` on the index,
 * or the same brand shows a different no-image count depending on which path
 * served it. So this mirrors `syncReferenceToAlgolia`'s three-way resolution in
 * watchlock exactly, including its quirks:
 *
 *  1. `images[0]` with a non-empty `storagePath` → has an image. ONLY the first
 *     entry is consulted; a storage path on `images[1]` alone does not count.
 *  2. Otherwise `imageUrls[0]`, if it is a string starting with `http`.
 *  3. Otherwise `imageUrl`, if it is a string starting with `http`.
 *
 * That is also why this is not `images.length === 0`: a ref whose picture
 * arrived as an extracted `imageUrls` entry or a legacy `imageUrl` has an image
 * as far as the index is concerned. If the indexer's rule changes, this has to
 * change with it.
 */
export function referenceHasNoImage(ref: {
  images?: Array<{ storagePath?: string } | null | undefined>;
  imageUrls?: unknown[];
  imageUrl?: unknown;
}): boolean {
  const first = Array.isArray(ref.images) ? ref.images[0] : undefined;
  if (first?.storagePath) return false;

  const firstUrl = Array.isArray(ref.imageUrls) ? ref.imageUrls[0] : undefined;
  if (typeof firstUrl === 'string' && firstUrl.startsWith('http')) return false;

  if (typeof ref.imageUrl === 'string' && ref.imageUrl.startsWith('http')) return false;

  return true;
}

/**
 * Flatten re-fetched listing pages into one hit list, keeping the first copy of
 * any record.
 *
 * Refresh re-fetches every page the admin has already loaded (Load More depth)
 * rather than collapsing to page 0. The pages are requested together, but they
 * are still separate requests: an indexer write landing between two of them
 * can shift a page boundary, so the same record could arrive twice. Deduping by
 * `objectID` makes that harmless; order is preserved so rows don't jump.
 */
export function mergeRefreshedHitPages<T extends { objectID: string }>(
  pages: Array<{ hits: T[] }>,
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const page of pages) {
    for (const hit of page.hits) {
      if (seen.has(hit.objectID)) continue;
      seen.add(hit.objectID);
      out.push(hit);
    }
  }
  return out;
}
